import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Chip, DataTable, Modal } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { formatDateTime } from "utils/time";

import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSSetpointsQuery } from "state/communities/communitiesApiSlice";

// Canonical mapping: legacy type strings → simplified logical type
const SETPOINT_TYPE_CANONICAL = {
  CONSUME: "CONSUME",
  INJECT: "INJECT",
  STORAGE_CHARGE: "CONSUME",
  EV_CHARGE: "CONSUME",
  CONSUMPTION: "CONSUME",
  STORAGE_DISCHARGE: "INJECT",
  EV_DISCHARGE: "INJECT",
  PRODUCTION: "INJECT",
};

const TYPE_TONE = { CONSUME: "info", INJECT: "amber" };
const TYPE_COLOR = { CONSUME: "var(--info)", INJECT: "var(--accent)" };

function SetpointsTable({ moduleType, startDatetime, endDatetime }) {
  const { t } = useTranslation(["community", "common"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const {
    data: setpointsData,
    isFetching,
    currentData,
  } = useGetEMSSetpointsQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 100, offset: 0 },
    { skip: !communityId, pollingInterval: 30000 }
  );
  // Pending for the *current* args (date range / module); a background
  // polling refetch keeps currentData and therefore does not flash a spinner.
  const isLoading = isFetching && currentData === undefined;

  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  const data = useMemo(() => setpointsData?.data || [], [setpointsData]);

  const rows = useMemo(
    () =>
      data.map((row, i) => ({
        id: row.id ?? `${row.datetime_timestamp}-${i}`,
        datetime: formatDateTime(row.datetime_timestamp),
        resource: row.resource_name || t("common:unknown"),
        type: SETPOINT_TYPE_CANONICAL[row.setpoint_type] || row.setpoint_type,
        value: row.setpoint_value,
        unit: row.resource_type === "HP" ? "°C" : "kW",
        meter: row.meter_name || "—",
        raw: row,
      })),
    [data, t]
  );

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  const typeChip = (canonical) => (
    <Chip tone={TYPE_TONE[canonical] || "gray"}>
      {t(`unified_ems.setpoint_type_${String(canonical).toLowerCase()}`, {
        defaultValue: canonical,
      })}
    </Chip>
  );

  const columns = [
    {
      key: "datetime",
      label: t("unified_ems.setpoints_table_datetime"),
      width: "22%",
      sortValue: (row) => new Date(row.raw?.datetime_timestamp).getTime() || 0,
    },
    { key: "resource", label: t("unified_ems.setpoints_table_resource") },
    { key: "type", label: t("unified_ems.setpoints_table_type"), render: (v) => typeChip(v) },
    {
      key: "value",
      label: t("unified_ems.setpoints_table_value"),
      align: "right",
      render: (v, row) => (
        <span className="tabular-nums" style={{ color: TYPE_COLOR[row.type], fontWeight: 600 }}>
          {`${Number(v).toFixed(3)} ${row.unit}`}
        </span>
      ),
    },
    { key: "meter", label: t("unified_ems.setpoints_table_meter") },
    {
      key: "actions",
      label: "",
      align: "right",
      sort: false,
      render: (_v, row) => (
        <button
          type="button"
          className="rec-icon-btn !w-[34px] !h-[34px]"
          title={t("unified_ems.setpoint_details")}
          onClick={() => setSelected(row.raw)}
        >
          <Icon name="visibility" style={{ fontSize: 18 }} />
        </button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-10 text-ink-3">
        <span className="animate-spin inline-flex">
          <Icon name="progress_activity" style={{ fontSize: 30, color: "var(--accent)" }} />
        </span>
      </div>
    );
  }

  const canonicalType = selected
    ? SETPOINT_TYPE_CANONICAL[selected.setpoint_type] || selected.setpoint_type
    : null;

  const IdField = ({ label, value }) => (
    <div>
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.4px] text-ink-3 mb-1">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12.5px] text-ink break-all">{value || "—"}</span>
        {value && (
          <button
            type="button"
            className="rec-icon-btn !w-[28px] !h-[28px] shrink-0"
            title={t("common:copy")}
            onClick={() => copy(value)}
          >
            <Icon name="content_copy" style={{ fontSize: 15 }} />
          </button>
        )}
      </div>
    </div>
  );

  IdField.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string };
  IdField.defaultProps = { value: null };

  const kv = (label, node) => (
    <div>
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.4px] text-ink-3 mb-1">
        {label}
      </div>
      <div className="text-[13.5px] text-ink">{node}</div>
    </div>
  );

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        search={t("common:search", { defaultValue: "Procurar…" })}
        searchKeys={["resource", "meter", "datetime"]}
        maxRows={10}
        emptyText={t("unified_ems.no_setpoints_generated")}
      />
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={t("unified_ems.setpoint_details")}
        subtitle={copied ? t("common:copied") : undefined}
        maxWidth={640}
        footer={
          <button type="button" className="rec-btn-ghost" onClick={() => setSelected(null)}>
            {t("common:close")}
          </button>
        }
      >
        {selected && (
          <div className="flex flex-col gap-[14px]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
              {kv(
                t("unified_ems.setpoints_table_datetime"),
                formatDateTime(selected.datetime_timestamp)
              )}
              {kv(
                t("unified_ems.setpoints_table_resource"),
                selected.resource_name || t("common:unknown")
              )}
              {kv(t("unified_ems.setpoints_table_type"), typeChip(canonicalType))}
              {kv(
                t("unified_ems.setpoints_table_value"),
                `${selected.setpoint_value.toFixed(3)} ${
                  selected.resource_type === "HP" ? "°C" : "kW"
                }`
              )}
              {kv(t("unified_ems.setpoints_table_meter"), selected.meter_name || "—")}
            </div>
            <div className="rec-divider" />
            <div className="flex flex-col gap-[12px]">
              <IdField
                label={t("unified_ems.setpoints.detail_modal.resource_id")}
                value={selected.resource_id}
              />
              <IdField
                label={t("unified_ems.setpoints.detail_modal.meter_id")}
                value={selected.meter_id}
              />
              <IdField
                label={t("unified_ems.setpoints.detail_modal.setpoint_id")}
                value={selected.id}
              />
            </div>
            {selected.metadata && Object.keys(selected.metadata).length > 0 && (
              <div>
                <div className="text-[11.5px] font-semibold uppercase tracking-[0.4px] text-ink-3 mb-1">
                  {t("unified_ems.setpoints.detail_modal.metadata")}
                </div>
                <pre
                  className="font-mono text-[12px] text-ink-2 m-0 overflow-auto"
                  style={{ background: "var(--surface-2)", padding: 12, borderRadius: 10 }}
                >
                  {JSON.stringify(selected.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

SetpointsTable.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
};

SetpointsTable.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
};

export default SetpointsTable;
